import { ContextMenu, DataGrid } from "devextreme-react";
import { Column, Editing, Paging, Scrolling } from "devextreme-react/data-grid";
import { useEffect, useMemo, useRef } from "react";
import { confirm } from "devextreme/ui/dialog";
import { formatNumber } from "devextreme/localization";
import { openPopup } from "@helpers/DxHelper";
import { useParams } from "react-router";
import { useAppSelector } from "@hooks/useAppSelector";
import { uqActions } from "@redux/uq/uq-slice";
import { useUniquePossibilitiesUpsert } from "@hooks/useUniquePossibilitiesUpsert";
import { useAppDispatch } from "@hooks/useAppDispatch";
import { CalculateGrid } from "@redux/uq/uq-actions";
import Assumptions from "./Assumptions";
import { useQueryClient } from "@tanstack/react-query";
import ColsButtons from "./ColsButtons";
import HiddenOption from "./HiddenOption";
import UpdatePopup from "./UpdatePopup";
import { createPortal } from "react-dom";
import UpdateComplexCell from "./UpdateComplexCell";
import HeaderTitle from "./HeaderTitle";
import PrepopulatePopup from "./PrepopulatePopup";
import UniquePossibilitiesSkeleton from "./UniquePossibilitiesSkeleton";
import CustomButton from "@/components/CustomButton";
import { Item } from "devextreme/ui/context_menu";
import SaveChangesPopup from "@/components/SaveChangesPopup";
import { NPVRow, RowEntity, UPRow, UniquePossibilities } from "@/types/types";
import ColaOverridePopup from "./ColaOverridePopup";
import PrintUPButton from "./PrintUPButton";
import PrintPreviewUPButton from "./PrintPreviewUPButton";
import { PopupRef } from "devextreme-react/cjs/popup";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import GridHeader from "../UPandNPVShared/GridHeader";
import GridCell from "../UPandNPVShared/GridCell";
import useUpdateRetirementAssets from "./useUpdateRetirementAssets";
import AgeCell from "../UPandNPVShared/AgeCell";
import { npvActions } from "@/redux/npv/npv-slice";

type Props = { initGrid?: UniquePossibilities };

export default function Grid({ initGrid }: Props) {
  const { id } = useParams();
  const {
    mutateAsync: updateUPAsync,
    isPending,
    isSuccess: isUpdateSuccess,
    data: upsertResponse,
  } = useUniquePossibilitiesUpsert();
  const updateRef = useRef<PopupRef>(null);
  const titleRef = useRef<PopupRef>(null);
  const colaOverridePopupRef = useRef<PopupRef>(null);
  const prepopulatePopupRef = useRef<PopupRef>(null);
  const dispatch = useAppDispatch();
  const {
    isModified,
    gridSet,
    settings,
    modifiedOn,
    headerTitles,
    showHiddenRows,
    reload,
    household,
  } = useAppSelector((state) => state.uq);
  const { age1: age1Now } = useAppSelector((state) => state.household);
  const queryClient = useQueryClient();

  const dataSource = useMemo(() => {
    const filteredData = gridSet?.filter((i: UPRow) => i.Age.value >= age1Now || !i.Age.value);
    const indexShift = gridSet?.length - filteredData?.length;

    dispatch(uqActions.setIndexShift(showHiddenRows ? 0 : indexShift));

    return {
      data: showHiddenRows ? gridSet : filteredData,
      indexShift: showHiddenRows ? 0 : indexShift,
    };
  }, [gridSet, showHiddenRows]);

  const saveUQ = async (grid = gridSet) => {
    await updateUPAsync({
      data: {
        modifiedOn: modifiedOn,
        modifiedBy: initGrid?.modifiedBy,
        household: { externalCode: id as string },
        forceSave: false,
        data: JSON.stringify({ gridSet: grid, settings, household, titles: headerTitles }),
      },
      overwrite: true,
    });
  };

  useEffect(() => {
    if (isUpdateSuccess) {
      if (!upsertResponse.hasNotice) {
        dispatch(uqActions.setIsModified(false));
        queryClient.setQueryData(["unique-possibilities", id], upsertResponse.data);
        queryClient.invalidateQueries({ queryKey: ["unique-possibilities", id] });
      } else {
        const result = confirm(
          `You are about to overwrite newer version of this unique possibilities, are you sure you want proceed?`,
          "Confirm changes"
        );
        result.then((dialogResult) => {
          if (dialogResult) {
            updateUPAsync({
              data: {
                modifiedOn: initGrid?.modifiedOn,
                modifiedBy: initGrid?.modifiedBy,
                household: { externalCode: id as string },
                data: JSON.stringify({ gridSet, settings }),
              },
              overwrite: true,
            });
          }
        });
      }

      if (upsertResponse.data.data != null) {
        let titlesToDispatch;
        if (typeof upsertResponse.data.data === "string") {
          const { titles } = JSON.parse(upsertResponse.data.data);
          titlesToDispatch = titles;
        } else {
          titlesToDispatch = upsertResponse.data.data.titles;
        }

        dispatch(npvActions.setTitles(titlesToDispatch));
      }
    }
  }, [isUpdateSuccess]);

  useUpdateRetirementAssets({ saveUpFn: saveUQ });

  if (reload || isPending) {
    return <UniquePossibilitiesSkeleton />;
  }

  const restoreToPreviousSave = async () => {
    await queryClient.invalidateQueries({ queryKey: ["unique-possibilities", id] });
    if (initGrid?.data) {
      dispatch(uqActions.setGrid(JSON.parse(initGrid.data).gridSet));
      dispatch(uqActions.setSettings(JSON.parse(initGrid.data).settings));
      await dispatch(CalculateGrid(0, JSON.parse(initGrid.data).gridSet));
      dispatch(uqActions.setIsModified(false));
    }
    dispatch(uqActions.setIsModified(false));
  };

  const btnsElement = document.getElementById("action-btns");

  return (
    <>
      {createPortal(
        <div className="flex items-center gap-4 pr-4 print:hidden">
          {initGrid?.data && isModified ? (
            <CustomButton
              onClickHandler={() => dispatch(uqActions.setShowRestorePopup(true))}
              text="Restore to previous save"
              disabled={isPending}
            />
          ) : null}
          {isModified ? (
            <CustomButton
              text="Save"
              variant="primary"
              onClickHandler={() => saveUQ()}
              disabled={isPending}
            />
          ) : null}
        </div>,
        btnsElement!
      )}
      <DataGrid
        className={`grid-custom-headers ao-grid grid-sticky-headers ${
          household?.isSingle ? "single-household" : "couple-household"
        }`}
        dataSource={dataSource.data}
        showBorders={true}
        showRowLines={true}
        showColumnLines={true}
        remoteOperations={false}
        wordWrapEnabled={true}
        onCellPrepared={(e) => {
          if (e.rowType === "header") {
            e.cellElement.style.backgroundColor = "var(--primary-color)";
            e.cellElement.style.color = "white";
          } else if (e.column.name == "Age" || e.column.name == "Age2") {
            e.cellElement.style.backgroundColor = "var(--primary-color)";
            e.cellElement.style.color = "white";
            e.cellElement.style.fontWeight = "500";
          }
        }}
      >
        <Column
          width={"8%"}
          dataField={"Age"}
          allowSorting={false}
          allowEditing={false}
          visible={!!household?.primaryContact?.birthDate && settings?.person1.showAge}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <AgeCell
                clientExternalCode={household?.primaryContact?.externalCode}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
          headerCellRender={() => (
            <GridHeader
              headerName={household?.primaryContact?.preferredFirstName ?? ""}
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
        />

        <Column
          width={"8%"}
          dataField={"Age2"}
          headerCellRender={() => (
            <GridHeader
              headerName={household?.secondaryContact?.preferredFirstName ?? ""}
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          allowEditing={false}
          allowSorting={false}
          visible={!!household?.secondaryContact?.birthDate && settings.person2?.showAge}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <AgeCell
                clientExternalCode={household?.secondaryContact?.externalCode}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
                isSecondary
              />
            );
          }}
        />

        <Column
          width={"fit"}
          dataField={"Retirement"}
          headerCellRender={() => (
            <GridHeader
              headerName="Retirement Assets"
              header="Retirement"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          allowSorting={false}
          alignment={"center"}
          allowEditing={true}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            const isFirstRow = data.row.data.Age.value === age1Now;

            if (isFirstRow)
              return (
                <GridCell
                  data={data as ColumnCellTemplateData<UPRow | NPVRow>}
                  prepopulatePopupRef={prepopulatePopupRef}
                  updateRef={updateRef}
                />
              );

            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"SocialSecurity"}
          headerCellRender={() => (
            <GridHeader
              headerName="Social Security"
              header="SocialSecurity"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          format={"currency"}
          visible={!household?.isSingle}
          allowSorting={false}
          alignment="center"
          allowEditing={false}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
        />
        <Column
          width={"fit"}
          dataField={"SocialSecurity1"}
          format={"currency"}
          allowSorting={false}
          alignment={"center"}
          allowEditing={false}
          visible={settings.showSS || household?.isSingle}
          headerCellRender={() => (
            <GridHeader
              headerName={household?.isSingle ? "Social Security" : "Social Security 1"}
              header="SocialSecurity1"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
        />
        <Column
          width={"fit"}
          dataField={"SocialSecurity2"}
          format={"currency"}
          headerCellRender={() => (
            <GridHeader
              headerName="Social Security 2"
              header="SocialSecurity2"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          visible={settings.showSS && !household?.isSingle}
          allowSorting={false}
          alignment={"center"}
          allowEditing={false}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
        />
        <Column
          width={"fit"}
          dataField={"Pension"}
          format={"currency"}
          allowSorting={false}
          allowEditing={true}
          headerCellRender={() => (
            <GridHeader
              headerName="Pension"
              header="Pension"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"Income"}
          headerCellRender={() => (
            <GridHeader
              headerName="Income 1"
              header="Income1"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          format={"currency"}
          allowSorting={false}
          allowEditing={true}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"Income2"}
          headerCellRender={() => (
            <GridHeader
              headerName="Income 2"
              header="Income2"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          format={"currency"}
          allowSorting={false}
          allowEditing={true}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"Spending"}
          format={"currency"}
          allowSorting={false}
          allowEditing={true}
          alignment={"center"}
          headerCellRender={() => (
            <GridHeader
              headerName="Spending 1"
              header="Spending"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"Spending2"}
          format={"currency"}
          allowEditing={true}
          allowSorting={false}
          alignment={"center"}
          headerCellRender={() => (
            <GridHeader
              headerName="Spending 2"
              header="Spending2"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"Spending3"}
          format={"currency"}
          allowSorting={false}
          allowEditing={true}
          alignment={"center"}
          headerCellRender={() => (
            <GridHeader
              headerName="Spending 3"
              header="Spending3"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => (
            <GridCell
              data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              prepopulatePopupRef={prepopulatePopupRef}
              updateRef={updateRef}
            />
          )}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Column
          width={"fit"}
          dataField={"YearEnd"}
          headerCellRender={() => (
            <GridHeader
              headerName="Year End"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          allowEditing={false}
          allowSorting={false}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => {
            const ror = data.row.data.ROR;
            const isCurrAgeRowOrLower =
              data.row.data.Age.value && age1Now >= data.row.data.Age.value;

            return (
              <div
                className={`relative px-2 py-[14px] text-sm print:px-1 print:py-1 print:!text-[10px] ${
                  data.value < 0 ? "text-danger" : ""
                } ${data.row.data.Age.value && age1Now > data.row.data.Age.value ? "italic" : ""}`}
              >
                {formatNumber(data.value, "currency").replaceAll("(", "").replaceAll(")", "")}
                {!ror ? null : (
                  <>
                    {(ror.note?.length && !ror.manualChange) ||
                    (ror.note?.length && isCurrAgeRowOrLower) ? (
                      <img
                        className="absolute right-0 top-0 print:h-2 print:w-2"
                        src="/icons/note-cell.svg"
                      />
                    ) : null}
                    {!ror.note?.length && ror.manualChange && !isCurrAgeRowOrLower ? (
                      <img
                        className="absolute right-0 top-0 print:h-2 print:w-2"
                        src="/icons/break-cell.svg"
                      />
                    ) : null}

                    {ror.note?.length && ror.manualChange && !isCurrAgeRowOrLower ? (
                      <img
                        className="absolute right-0 top-0 print:h-2 print:w-2"
                        src="/icons/note-break-cell.svg"
                      />
                    ) : null}
                  </>
                )}
              </div>
            );
          }}
        />

        <Column
          width={"fit"}
          dataField={"ROR"}
          headerCellRender={() => (
            <GridHeader
              headerName="ROR"
              header="ROR"
              colaOverridePopupRef={colaOverridePopupRef}
              titleRef={titleRef}
            />
          )}
          allowSorting={false}
          allowEditing={true}
          visible={settings.showRor}
          alignment={"center"}
          cellRender={(data: ColumnCellTemplateData<UPRow>) => {
            const value = data.value as RowEntity;
            const isCurrAgeRowLower = data.row.data.Age.value && age1Now >= data.row.data.Age.value;

            const ctxMenuArr: Item[] = [
              { text: "Reset", icon: "revert", visible: value.manualChange },
              { text: "Note", icon: "comment" },
            ];

            return (
              <>
                <div
                  id={`ror-${data.rowIndex}`}
                  className={`relative cursor-pointer px-2 py-[14px] text-sm print:px-1 print:py-1 print:!text-[10px] ${
                    data.row.data.Age.value && age1Now > data.row.data.Age.value ? "italic" : ""
                  }`}
                >
                  <div>{(value.value * 100).toPrecision(2) + "%"}</div>
                  {(value.note?.length && !value.manualChange) ||
                  (value.note?.length && isCurrAgeRowLower) ? (
                    <img
                      className="absolute right-0 top-0 print:h-2 print:w-2"
                      src="/icons/note-cell.svg"
                    />
                  ) : null}
                </div>

                <ContextMenu
                  items={ctxMenuArr}
                  width={200}
                  target={`#ror-${data.rowIndex}`}
                  onItemClick={(e) => {
                    switch (e.itemData?.text?.toLowerCase()) {
                      case "reset": {
                        const newGrid = structuredClone(gridSet);
                        newGrid[data.rowIndex + dataSource.indexShift].ROR = {
                          value: value.defaultValue,
                          defaultValue: value.defaultValue,
                          note: value.note,
                          manualChange: false,
                        };

                        dispatch(CalculateGrid(data.rowIndex + dataSource.indexShift, newGrid));
                        break;
                      }
                      case "note":
                        dispatch(
                          uqActions.setUpdateData({
                            value: value.value,
                            defaultValue: value.defaultValue,
                            manualChange: value.manualChange,
                            note: value.note ?? "",
                            id: `ror-${data.rowIndex}`,
                            col: "ROR",
                            rowIndex: data.rowIndex,
                            row: dataSource.indexShift + data.rowIndex,
                          })
                        );
                        openPopup(updateRef);
                        break;
                    }
                  }}
                />
              </>
            );
          }}
          editCellRender={(data: ColumnCellTemplateData<UPRow>) => {
            return (
              <UpdateComplexCell
                gridSet={gridSet}
                data={data as ColumnCellTemplateData<UPRow | NPVRow>}
              />
            );
          }}
        />
        <Editing mode="cell" allowUpdating={true} />
        <Scrolling showScrollbar="onScroll" scrollByThumb={true} scrollByContent={true} />
        <Paging defaultPageSize={200} />
      </DataGrid>
      <div className="relative flex flex-wrap items-end gap-8">
        <Assumptions />
        <PrintUPButton
          hasUPData={Boolean(initGrid?.data)}
          externalCode={initGrid?.household?.externalCode as string}
        />
        <PrintPreviewUPButton
          hasUPData={gridSet?.length > 0}
          label="Print preview"
          upData={initGrid}
        />
        <HiddenOption />
      </div>
      <ColsButtons />
      <UpdatePopup gridSet={gridSet} updateRef={updateRef} />
      <PrepopulatePopup popupRef={prepopulatePopupRef} gridSet={gridSet} />
      <ColaOverridePopup popupRef={colaOverridePopupRef} gridSet={gridSet} />
      {isModified ? (
        <SaveChangesPopup restoreUp restoreUpFn={restoreToPreviousSave} upData={initGrid} />
      ) : null}
      <HeaderTitle data={headerTitles} popupRef={titleRef} />
    </>
  );
}
